<template>
  <label
    v-if="element.type === 'singleLabel' && element.labelSize !== 0"
    :for="`${name}_${element.row}_${elementIdx}`"
    :class="getClass(element)"
  >
    {{ $t(`smartChart.${element.label}`) }}
  </label>
  <p
    v-else-if="element.type === 'comment'"
    :class="getClass(element)"
    class="mb-0"
  >
    {{ element.label }}
  </p>
  <div
    v-else-if="
      ['text', 'date', 'dropdown', 'dropdownGroup', 'time', 'radio'].includes(
        element.type
      )
    "
    :class="getClass(element)"
  >
    <b-form-input
      v-if="element.type === 'text'"
      :id="`${name}_${element.row}_${elementIdx}`"
      :value="value"
      :readonly="viewing || element.readonly"
      :placeholder="element.placeholder"
      @input="v => $emit('change', v, element.name)"
    ></b-form-input>
    <b-form-input
      v-if="element.type === 'date' && finalized"
      :id="`${name}_${element.row}_${elementIdx}`"
      :value="formatVDDate(value, { format: 'MM/DD/YYYY', lang: $i18n.locale })"
      :readonly="true"
      :placeholder="element.placeholder"
    ></b-form-input>
    <b-form-input
      v-if="element.type === 'time' && finalized"
      :id="`${name}_${element.row}_${elementIdx}`"
      :value="formatVDDate(value, { format: 'HH:mm', lang: $i18n.locale })"
      :readonly="true"
      :placeholder="element.placeholder"
    ></b-form-input>

    <el-date-picker
      v-else-if="element.type === 'date' && !finalized"
      :id="`${name}_${element.row}_${elementIdx}`"
      format="dd/MM/yyyy"
      class="w-100 cr__date__picker"
      v-model="dateValue"
      :editable="false"
      :readonly="viewing || element.readonly"
      :disabled="viewing || element.readonly"
      @change="v => $emit('change', v, element.name)"
      size="small"
      :clearable="false"
    >
    </el-date-picker>
    <el-time-picker
      v-else-if="element.type === 'time' && !finalized"
      :id="`${name}_${element.row}_${elementIdx}`"
      format="HH:mm"
      class="w-100"
      v-model="dateValue"
      :picker-options="{
        selectableRange: '00:00:00 - 11:59:59',
      }"
      :readonly="viewing || element.readonly"
      :disabled="viewing || element.readonly"
      @change="v => $emit('change', v, element.name)"
      size="small"
    >
    </el-time-picker>
    <b-form-radio-group
      v-else-if="element.type === 'radio'"
      :id="`${name}_${element.row}_${elementIdx}`"
      :readonly="viewing || element.readonly"
      :disabled="viewing || element.readonly"
      v-model="dateValue"
      :options="getSelectOptions(element)"
      @change="v => $emit('change', v, element.name)"
    ></b-form-radio-group>
    <b-form-select
      v-else-if="element.type === 'dropdown'"
      :readonly="viewing || element.readonly"
      :disabled="viewing || element.readonly"
      :value="value"
      @input="v => $emit('change', v, element.name)"
      :options="getSelectOptions(element)"
      :id="`${name}_${element.row}_${elementIdx}`"
    >
    </b-form-select>
    <select
      v-else-if="element.type === 'dropdownGroup'"
      class="custom-select"
      :readonly="viewing || element.readonly"
      :disabled="viewing || element.readonly"
      :value="value"
      @input="v => $emit('change', v, element.name)"
      :id="`${name}_${element.row}_${elementIdx}`"
    >
      <template v-for="(item, idx) in getSelectOptions(element)">
        <option :value="item.value" v-if="!item.label" :key="idx">{{
          item.text
        }}</option>
        <optgroup v-else :label="item.label" :key="`${idx}_2`">
          <option
            v-for="(subItem, i) in item.options"
            :key="i"
            :value="subItem.value"
            >{{ subItem.text }}</option
          >
        </optgroup>
      </template>
    </select>
    <div class="input-group-append" v-if="element.suffix">
      <span class="input-group-text">{{ element.suffix }}</span>
    </div>
  </div>
  <hr
    v-else-if="element.type === 'hr'"
    :key="`${elementIdx}_1`"
    class="w-100"
  />
  <div class="col form-group" v-else-if="element.type === 'textarea'">
    <label>
      {{ $t(`smartChart.${element.label}`) }}
    </label>
    <p class="as__textarea" v-if="isPrint">{{ value }}</p>
    <b-form-textarea
      v-else
      :id="`${name}_${element.row}_${elementIdx}`"
      :value="value"
      @input="v => $emit('change', v, element.name)"
      :readonly="viewing || element.readonly"
      :placeholder="element.placeholder"
      :rows="5"
    ></b-form-textarea>
  </div>
  <div
    class="medicines__used row col my-1"
    v-else-if="element.type === 'medicines'"
    :key="`${elementIdx}_3`"
  >
    <div
      v-for="(medicine, idx) in medicinesToChunksV2()"
      class="col-6"
      :key="idx"
    >
      <MedicineWidget
        :medicine="medicine"
        v-if="medicine.length > 0"
      ></MedicineWidget>
    </div>
  </div>
  <!-- protocol -->
  <div
    class="form-group row no-gutters col mb-0"
    v-else-if="element.type === 'stcProtocol'"
  >
    <div :class="`col-${element.controlSize || 6}`">
      <b-form-select
        :readonly="viewing || element.readonly"
        :disabled="viewing || element.readonly"
        v-model="dateValue"
        @input="v => $emit('change', v, element.name)"
        :options="getSelectOptions(element)"
        :id="`${name}_${element.row}_${elementIdx}`"
        class="w-40"
      >
      </b-form-select>
      <b-form-input
        v-if="dateValue === 'OTHERS'"
        class="w-40 inline ml-2"
        :value="linkedValue"
        :readonly="viewing || element.readonly"
        @input="v => $emit('change', v, `${element.name}Others`)"
      ></b-form-input>
    </div>
  </div>
  <!-- morphology which has very very long label -->
  <div class="col" v-else-if="element.type.startsWith('group')">
    <div class="form-group row  no-gutters mb-0">
      <label
        for="ICSIReportForm_morph"
        :class="
          `col-7 col-form-label px-1 ${
            element.labelAlign === 'right' ? 'text-right' : ''
          }`
        "
        >{{ $t(`smartChart.${element.label}`) }}</label
      >
      <div :class="`col${element.suffix ? ' input-group' : ''}`">
        <b-form-input
          :id="`${name}_${element.row}_`"
          :value="value"
          :readonly="viewing || element.readonly"
          :placeholder="element.placeholder"
          @input="v => $emit('change', v, element.name)"
          v-if="element.type.endsWith('text')"
        ></b-form-input>
        <b-form-select
          v-else-if="element.type.endsWith('dropdown')"
          :readonly="viewing || element.readonly"
          :disabled="viewing || element.readonly"
          :value="value"
          @input="v => $emit('change', v, element.name)"
          :options="getSelectOptions(element)"
          :id="`${name}_${element.row}_`"
        >
        </b-form-select>
        <div class="input-group-append" v-if="element.suffix">
          <span class="input-group-text">{{ element.suffix }}</span>
        </div>
      </div>
    </div>
    <p class="m-0" v-if="element.comment">
      <small>
        {{ $t(`smartChart.${element.comment}`) }}
      </small>
    </p>
  </div>
</template>
<script>
/* eslint-disable arrow-parens */
import _ from 'lodash';
import Dateformatter from '@/components/mixins/dateformatter';
import MedicineWidget from './MedicineWidget.vue';

export default {
  name: 'ReportFormWidget',
  mixins: [Dateformatter],
  components: { MedicineWidget },
  props: {
    name: String,
    element: Object,
    extraClass: String,
    elementIdx: Number || String,
    value: null,
    viewing: {
      type: Boolean,
      default: false,
    },
    linkedValue: null,
    tik: null,
    isPrint: {
      type: Boolean,
      default: false,
    },
    finalized: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dateValue: this.value,
    };
  },
  methods: {
    getSelectOptions(element) {
      const { values, selectOptions, rawOptions } = element;
      if (rawOptions) {
        return JSON.parse(rawOptions);
      }
      const options = selectOptions.split(',');
      const result = values.split(',').map((v, i) => ({
        value: v,
        text: options[i],
      }));
      return result;
    },
    getClass(element) {
      let cls = '';
      if (element.type === 'singleLabel') {
        cls = `col-${element.labelSize || 4} col-form-label pr-1 `;
        if (element.offset > 0) {
          cls += `offset-${element.offset}`;
        }
        if (element.labelAlign === 'right') {
          cls += ' text-right ';
        }
        return cls;
      }
      if (element.type === 'comment') {
        if (element.labelAlign === 'right') {
          cls += 'col text-right';
        }
        return cls;
      }
      cls = `col col-${element.controlSize || 6} `;
      if (element.suffix) {
        cls += 'input-group ';
      }
      return cls;
    },
    medicinesToChunks() {
      if (this.element.type !== 'medicines') return null;
      const groupCount = 2;
      const keyGroups = _.chunk(Object.keys(this.value), groupCount);
      const result = [];
      function medsObjectToString(medsObject) {
        return Object.entries(medsObject).map(([k, v]) => `${k}: ${v}`);
      }
      keyGroups.forEach(keyGroup => {
        const groupResult = [];
        keyGroup.forEach(key => {
          const rr = {
            [key]: medsObjectToString(this.value[key]),
          };
          groupResult.push(rr);
        });
        result.push(groupResult);
      });
      return result;
    },
    medicinesToChunksV2() {
      if (this.element.type !== 'medicines') return null;
      if (!this.value || _.isEmpty(this.value)) return null;
      const result = [];
      function medsObjectToString(medsObject) {
        return Object.entries(medsObject).map(([k, v]) => `${k}: ${v}`);
      }
      Object.keys(this.value).forEach(key => {
        const medObject = this.value[key];
        const meds = medsObjectToString(medObject);
        meds.forEach(med => {
          result.push({
            key,
            value: med,
          });
        });
      });
      if (result.length === 1) {
        return [result, []];
      }

      const half = Math.floor(result.length / 2);
      return [result.slice(0, half), result.slice(half, result.length)];
    },
  },
};
</script>
<style lang="scss">
.form-group > label {
  text-transform: none !important;
}
.w-40 {
  width: 40% !important;
}
.inline {
  display: inline-block;
}
.input-group {
  align-self: flex-start; /* fix a bug in bootstrap 4 */
  .input-group-append {
    .input-group-text {
      padding: 0.25rem;
    }
  }
}
.cr__date__picker {
  .el-input__inner {
    padding-right: 0 !important;
  }
}
</style>
